import { IconUser16 } from '@warp-ds/icons/react/user-16';
import { Alert, Button, TextField, Toggle } from '@warp-ds/react';
import cn from 'classnames';
import type { FormEvent } from 'react';
import { useMemo } from 'react';
import { t } from '../../locale/texts';
import { getOneYearFromToday } from '../../util/datetime-fns';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { CustomSelect } from '../common/CustomSelect';
import { Dropdown } from '../common/Dropdown';
import { FrontPageModal } from '../common/frontPageModal/FrontPageModal';
import { FrontpageDatePickerWrapper } from '../common/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import type { AutocompleteOption, AutocompleteResponse, MonthDuration, MonthPickerMonth, PrepackageSearchState } from '../types';
import { dateSelectionOptions } from './data/dateSelectionOptions';
import { PrepackageSearchOriginOptions } from './data/PrepackageSearchOriginOptions';
import { FlexibleDateSelection } from './FlexibleDateSelection';
import { PaxCombination } from './PaxCombination';
import { PrepackageHiddenInputs } from './PrepackageHiddenInputs';
import styles from './PrepackageSearch.module.css';

export type PrepackageAutoCompleteData = {
    ppAutocompleteUrl: string;
    autocompleteMapper: (options: AutocompleteResponse) => AutocompleteOption[];
    onDestinationSelect: (option: AutocompleteOption | null, typedText?: string) => void;
    headers?: HeadersInit;
    initialOptions?: AutocompleteOption[];
    initialOptionsTitle?: string;
    tip?: { title: string; body: string };
};

export type PrepackageSearchFormProps = {
    state: PrepackageSearchState;
    ready: boolean;
    ppResultUrl?: string;
    invalid: Record<string, boolean>;
    validationErrors: Partial<Record<'departureDate' | 'returnDate' | 'returnDateAfterDeparture', string | null>>;
    hasNoOffers?: boolean;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
    onSearchTypeChange: (searchType: PrepackageSearchState['searchType']) => void;
    onOriginAirportChange: (originAirport: string) => void;
    onToggleTravelMonth: (month: MonthPickerMonth) => void;
    onDurationChange: (duration: MonthDuration) => void;
    onDatesChange: (dates: { startDate: Date | null; endDate: Date | null }) => void;
    autoCompleteData?: PrepackageAutoCompleteData;
    onPaxChange: (adults: number, childrenAges: number[]) => void;
    compactTop?: boolean;
};

export const PrepackageSearchForm = ({
    state,
    ready,
    ppResultUrl,
    invalid,
    validationErrors,
    hasNoOffers,
    onSubmit,
    onSearchTypeChange,
    onOriginAirportChange,
    onToggleTravelMonth,
    onDurationChange,
    onDatesChange,
    autoCompleteData,
    onPaxChange,
    compactTop = false,
}: PrepackageSearchFormProps) => {
    const selectedDateSelectionOptions = useMemo(
        () => (ready && state.searchType ? dateSelectionOptions.filter((opt) => opt.value === state.searchType) : []),
        [ready, state.searchType],
    );

    const joinErrors = (errors: Array<string | null | undefined>): string => {
        const parts = Array.from(new Set(errors.filter((e): e is string => Boolean(e))));

        if (parts.length === 0) return '';
        if (parts.length === 1) return parts[0];

        const depMsg = t('prepackage.validation.departureDate');
        const retMsg = t('prepackage.validation.returnDate');

        if (parts.includes(depMsg) && parts.includes(retMsg)) {
            return t('prepackage.validation.bothDates');
        }

        if (parts.length === 2) return `${parts[0]} og ${parts[1]}`;
        return parts.join('. ');
    };

    const anchorErrorText = joinErrors([
        invalid.departureDate ? validationErrors.departureDate : undefined,
        invalid.returnDate ? validationErrors.returnDate : undefined,
        invalid.returnDateAfterDeparture ? validationErrors.returnDateAfterDeparture : undefined,
    ]);

    const adultText = state.numberOfAdults === 1 ? '1 voksen' : `${state.numberOfAdults} voksne`;
    const childrenText = state.agesOfChildren.length > 0 ? `, ${state.agesOfChildren.length} barn` : '';
    const paxTriggerLabel = `${adultText}${childrenText}`;

    return (
        <SearchPanelWrapper onSubmit={onSubmit} action={ppResultUrl} method="GET" dataTestId="prepackageSearch" compactTop={compactTop}>
            <div className="sm:hidden">
                <Toggle
                    type="radio"
                    options={dateSelectionOptions}
                    selected={selectedDateSelectionOptions}
                    onChange={(selected) => onSearchTypeChange(selected.value)}
                    className="flex flex-row gap-8"
                />
            </div>
            <div className="hidden sm:flex flex-row gap-8">
                <CustomSelect
                    slim
                    fitContent
                    options={dateSelectionOptions}
                    value={ready && state.searchType ? state.searchType : ''}
                    onChange={(value) => onSearchTypeChange(value as PrepackageSearchState['searchType'])}
                />
                <Dropdown slim className="w-fit" trigger={{ text: paxTriggerLabel }}>
                    <PaxCombination numberOfAdults={state.numberOfAdults} agesOfChildren={state.agesOfChildren} updatePax={onPaxChange} />
                </Dropdown>
            </div>
            <div className={styles['form-body']}>
                <CustomSelect
                    relative
                    fullWidth
                    fullscreenTitle="Hvor vil du reise fra?"
                    triggerLabel={t('prepackage.origin.label')}
                    options={PrepackageSearchOriginOptions}
                    value={state.originAirport}
                    onChange={onOriginAirportChange}
                />
                {autoCompleteData && (
                    <Autocomplete
                        vertical="prepackage"
                        id="destination"
                        label={t('prepackage.destination.label')}
                        placeholder={t('prepackage.destination.placeholder')}
                        defaultValue={state.destination}
                        url={autoCompleteData.ppAutocompleteUrl}
                        headers={autoCompleteData.headers}
                        mapOptions={autoCompleteData.autocompleteMapper}
                        departure={false}
                        onSelect={autoCompleteData.onDestinationSelect}
                        initialOptions={autoCompleteData.initialOptions}
                        initialOptionsTitle={autoCompleteData.initialOptionsTitle}
                        tip={autoCompleteData.tip}
                    />
                )}
                {state.searchType === 'flexible' ? (
                    <FlexibleDateSelection
                        travelMonths={state.travelMonths}
                        duration={state.duration}
                        toggleMonth={onToggleTravelMonth}
                        setDuration={onDurationChange}
                    />
                ) : (
                    <FrontpageDatePickerWrapper
                        upperDateLimit={getOneYearFromToday()}
                        startDate={state.departureDate}
                        endDate={state.returnDate}
                        useRange
                        onDateChange={onDatesChange}
                        className={styles['date-picker']}
                        useAnchor={{
                            invalid: invalid.departureDate || invalid.returnDate || invalid.returnDateAfterDeparture,
                            helpText: anchorErrorText || undefined,
                        }}>
                        <TextField
                            id="date-range-start"
                            label={t('prepackage.departureLabel')}
                            invalid={invalid.departureDate}
                            helpText={validationErrors.departureDate || undefined}
                        />
                        <TextField
                            id="date-range-end"
                            label={t('prepackage.returnLabel')}
                            invalid={invalid.returnDate || invalid.returnDateAfterDeparture}
                            helpText={(validationErrors.returnDate ?? validationErrors.returnDateAfterDeparture) || undefined}
                        />
                    </FrontpageDatePickerWrapper>
                )}
                <FrontPageModal breakpoint="sm" title="Reisende" trigger={{ text: paxTriggerLabel, prefix: <IconUser16 aria-hidden /> }}>
                    <PaxCombination numberOfAdults={state.numberOfAdults} agesOfChildren={state.agesOfChildren} updatePax={onPaxChange} />
                </FrontPageModal>
                <Button
                    className={cn(styles['search-button'], {
                        [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                        [styles['search-button--error-return-after-departure']]: invalid.returnDateAfterDeparture,
                    })}
                    fullWidth
                    type="submit"
                    primary
                    data-testid="prepackageSearchButtonNew">
                    {t('action.search')}
                </Button>
            </div>
            <PrepackageHiddenInputs
                originAirport={state.originAirport}
                destination={state.destination}
                destinationCountry={state.destinationCountry}
                boundingBox={state.boundingBox}
                departureDate={state.departureDate}
                returnDate={state.returnDate}
                flexibleDeparture={state.flexibleDeparture}
                travelMonths={state.travelMonths}
                duration={state.duration}
                searchType={state.searchType}
                numberOfAdults={state.numberOfAdults}
                agesOfChildren={state.agesOfChildren}
            />
            {hasNoOffers && (
                <Alert type="info" show data-testid="noOffersWarning" className="mt-8">
                    {t('prepackage.validation.noOffers')}
                </Alert>
            )}
        </SearchPanelWrapper>
    );
};
